/**
 * material.repository.ts — 원재료 카탈로그(material_categories) Firestore 격리 layer.
 */
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  Timestamp,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "./firebase";
import { tcol } from "@/domain/tenantPath";
import type { MaterialCategory, MaterialCategoryKind } from "@/domain/material";
import { LINE_CODES, type LineCode } from "@/domain/material_mapping";

const COL = "material_categories";

const VALID_KINDS: MaterialCategoryKind[] = ["결합제", "굵은골재", "잔골재", "혼화제", "기타"];

/** Firestore에 옛 분류명으로 저장된 데이터를 새 이름으로 매핑 (점진 마이그레이션 호환) */
const LEGACY_CATEGORY_ALIAS: Readonly<Record<string, MaterialCategoryKind>> = {
  "골재": "굵은골재",
  "시멘트": "결합제",
  "혼화재": "결합제",
};

function fromFirestore(snap: QueryDocumentSnapshot<DocumentData>): MaterialCategory {
  const d = snap.data();
  const rawCat = typeof d.category === "string" ? d.category : "";
  const aliased = LEGACY_CATEGORY_ALIAS[rawCat];
  const kind = aliased
    ? aliased
    : (VALID_KINDS as string[]).includes(rawCat)
      ? (rawCat as MaterialCategoryKind)
      : "기타";
  const updatedAt = d.updated_at;
  return {
    code: snap.id,
    name: d.name ?? "",
    category: kind,
    sort_order: typeof d.sort_order === "number" ? d.sort_order : undefined,
    unit: typeof d.unit === "string" && d.unit.trim() ? d.unit : undefined,
    conversion_factor:
      typeof d.conversion_factor === "number" && d.conversion_factor > 0 ? d.conversion_factor : undefined,
    is_active: d.is_active === false ? false : undefined,
    pricing_window:
      typeof d.pricing_window === "number" && d.pricing_window > 0 ? d.pricing_window : undefined,
    pricing_auto: d.pricing_auto === true ? true : undefined,
    outbound_subgroup:
      typeof d.outbound_subgroup === "string" && d.outbound_subgroup.trim()
        ? d.outbound_subgroup.trim()
        : undefined,
    unify_group:
      typeof d.unify_group === "string" && d.unify_group.trim() ? d.unify_group.trim() : undefined,
    unify_enabled: d.unify_enabled === true ? true : undefined,
    default_line_code: (LINE_CODES as readonly string[]).includes(d.default_line_code)
      ? (d.default_line_code as LineCode)
      : undefined,
    updated_at: updatedAt instanceof Timestamp ? updatedAt.toDate() : undefined,
  };
}

/**
 * 쓰기용 타입 — 값을 비우는 경우를 표현한다.
 *
 * upsert 는 merge:true + undefined 필드 제거라, undefined 를 넘기면 "안 건드림"이 되어
 * 기존 값이 그대로 남는다. 그래서 "지정 안 함"으로 되돌릴 땐 빈 문자열/false 를 넘긴다
 * (fromFirestore 가 빈 값을 undefined 로 읽으므로 결과는 동일).
 */
export type MaterialCategoryWrite = Omit<MaterialCategory, "default_line_code"> & {
  default_line_code?: LineCode | "";
};

export const materialRepo = {
  watch(cb: (rows: MaterialCategory[]) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      collection(db, tcol(COL)),
      (snap) => cb(snap.docs.map(fromFirestore)),
      onError,
    );
  },

  async upsert(cat: MaterialCategoryWrite): Promise<void> {
    const { code, updated_at: _ignored, ...rest } = cat;
    void _ignored;
    // Firestore는 undefined 필드를 허용하지 않음 — 제거 후 setDoc
    const cleaned = Object.fromEntries(
      Object.entries(rest).filter(([, v]) => v !== undefined),
    );
    await setDoc(
      doc(db, tcol(COL), code),
      { ...cleaned, updated_at: Timestamp.now() },
      { merge: true },
    );
  },

  /** 여러 카테고리 일괄 upsert (gasCall: master.prices.upsert 대응) */
  async upsertBatch(cats: MaterialCategoryWrite[]): Promise<void> {
    const batch = writeBatch(db);
    const now = Timestamp.now();
    for (const cat of cats) {
      const { code, updated_at: _ignored, ...rest } = cat;
      void _ignored;
      // Firestore는 undefined 필드를 허용하지 않음 — 제거 후 set
      const cleaned = Object.fromEntries(
        Object.entries(rest).filter(([, v]) => v !== undefined),
      );
      batch.set(doc(db, tcol(COL), code), { ...cleaned, updated_at: now }, { merge: true });
    }
    await batch.commit();
  },

  /**
   * 자재 삭제 — material_categories 문서 삭제만. 외래키(raw_materials/supplier_prices/material_inbound/material_mappings) 정리는
   * 호출자가 사전 확인 후 진행 책임. 데이터 정합성을 위해 호출 전 사용 여부 검사 권장.
   */
  async remove(code: string): Promise<void> {
    await deleteDoc(doc(db, tcol(COL), code));
  },
};
